import React, { useEffect, useState } from 'react';
import Avatar from 'react-avatar';
import MsgSend from '../../shared/assets/svg/msgSendIcon.svg';
import DownloadIcon from '../../shared/assets/svg/download.svg';
import LikeIcon from '../../shared/assets/png/like.png';
import FilledLikeIcon from '../../shared/assets/png/filledLike.png';
import CommentIcon from '../../shared/assets/png/comment.png';
import TextareaAutosize from 'react-textarea-autosize';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  editPost,
  createComment,
  createReply,
  createLike,
  deleteLike,
  getUsersByChar
} from '../services/api/implementation/impl';
import Slider from 'react-slick';
import socket from '../../../socket'; // Import socket

// Import slick styles (required)
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

function LiveFeedCard({ data, onSuccess }) {
  const userId = Cookies.get('userId');
  const [mainComment, setMainComment] = useState('');
  const [comments, setComments] = useState(data?.comments || []);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [visibleComments, setVisibleComments] = useState(2);
  const [visibleReplies, setVisibleReplies] = useState({});
  const [isPublic, setIsPublic] = useState(data?.is_public || false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(data?.is_liked || false); // Initialize based on data
  const [tagedUser, setTagedUser] = useState([]);
  const [userList, setUsersList] = useState([]);

  // Helper: Build tag info from module/method (with fallbacks)
  const buildTagInfo = () => {
    const module = (data?.module || '').toString().trim();
    const methodRaw = (data?.method || '').toString().trim();

    const normalizeMethod = (m) => {
      const lower = m.toLowerCase();
      if (!m) return '';
      if (lower === 'post') return 'Created';
      if (lower === 'create') return 'Created';
      if (lower === 'update' || lower === 'put' || lower === 'patch') return 'Updated';
      if (lower === 'delete' || lower === 'remove') return 'Deleted';
      return m.charAt(0).toUpperCase() + m.slice(1).toLowerCase();
    };

    const iconFor = (mod, content) => {
      const text = `${mod} ${content || ''}`.toLowerCase();
      const modLower = mod.toLowerCase();
      if (modLower === 'partner' || modLower === 'lead' || text.includes('lead'))
        return '/icons/user.svg';
      if (text.includes('rfq') || text.includes('quotation')) return '/icons/document.svg';
      if (text.includes('requirement')) return '/icons/document.svg';
      if (text.includes('block') || text.includes('level') || text.includes('unit'))
        return '/icons/organisation.svg';
      if (text.includes('task')) return '/icons/bell.svg';
      return '/icons/pin.svg';
    };

    // Primary: use module + method
    if (module && methodRaw) {
      const method = normalizeMethod(methodRaw);
      return { label: `${method} ${module}`, icon: iconFor(module, '') };
    }

    // Fallback: derive from post text
    const postText = (data?.post || '').toLowerCase();
    if (postText) {
      if (postText.includes('rfq')) return { label: 'RFQ Created', icon: iconFor('rfq', postText) };
      if (postText.includes('quotation'))
        return { label: 'Quotation Created', icon: iconFor('quotation', postText) };
      if (postText.includes('requirement'))
        return { label: 'Requirement Created', icon: iconFor('requirement', postText) };
      if (postText.includes('task update'))
        return { label: 'Task Updated', icon: iconFor('task', postText) };
      if (postText.includes('block'))
        return { label: 'Block Updated', icon: iconFor('block', postText) };
      if (postText.includes('level'))
        return { label: 'Level Updated', icon: iconFor('level', postText) };
      if (postText.includes('unit'))
        return { label: 'Unit Updated', icon: iconFor('unit', postText) };
      if (postText.includes('accommodation'))
        return { label: 'Accommodation Updated', icon: iconFor('accommodation', postText) };
      if (postText.includes('partner'))
        return { label: 'Partner Created', icon: iconFor('partner', postText) };
      if (postText.includes('service provider'))
        return { label: 'Service Provider Created', icon: iconFor('service', postText) };
      if (postText.includes('user'))
        return { label: 'User Created', icon: iconFor('user', postText) };
      if (postText.includes('lead'))
        return { label: 'Lead Created', icon: iconFor('lead', postText) };
    }

    return null;
  };

  const tagInfo = buildTagInfo();

  // Socket listener for comments

  // Track page view
  useEffect(() => {
    mixpanel.track('Live Feed Card Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    const handleComment = (res) => {
      console.log('Comment response', res);
      if (res.post_id === data?.id) {
        setComments((prevComments) => [...prevComments, res]);
      }
    };

    const handleReply = (res) => {
      console.log('Reply response', res);
      if (res.post_id === data?.id) {
        setComments((prevComments) =>
          prevComments.map((comment) =>
            comment?.id === res.comment_id
              ? {
                  ...comment,
                  replies: Array.isArray(comment?.replies) ? [...comment.replies, res] : [res]
                }
              : comment
          )
        );
      }
    };

    const handleTagInPost = (res) => {
      console.log('Tag in Post', res);
    };

    const handleTagInComment = (res) => {
      console.log('Tag in comment', res);
    };

    const handleLike = (res) => {
      console.log('Like Response', res);
      if (res.post.post_id === data.id) {
        setLikes(res.count); // Update likes count based on the incoming data
      }
    };

    socket.on('Comment', handleComment);
    socket.on('Reply', handleReply);
    socket.on('Like', handleLike);
    socket.on('TAG IN POST', handleTagInPost);
    socket.on('TAG IN COMMENT', handleTagInComment);

    // Cleanup socket listeners on unmount
    return () => {
      socket.off('Comment', handleComment);
      socket.off('Reply', handleReply);
      socket.off('Like', handleLike);
      socket.off('TAG IN POST', handleTagInPost);
      socket.off('TAG IN COMMENT', handleTagInComment);
    };
  }, [data?.id]);

  // Helper to check if file is an image
  const isImage = (fileNameOrUrl = '') => {
    const lowerName = fileNameOrUrl.toLowerCase();
    return (
      lowerName.endsWith('.jpg') ||
      lowerName.endsWith('.jpeg') ||
      lowerName.endsWith('.png') ||
      lowerName.endsWith('.gif') ||
      lowerName.endsWith('.webp')
    );
  };

  // Separate attachments
  const imageAttachments = data?.attachments?.filter((attach) => isImage(attach.name));
  const fileAttachments = data?.attachments?.filter((attach) => !isImage(attach.name));

  // Slider settings
  const sliderSettings = {
    dots: true, // Show dots
    arrows: false, // Hide arrows
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1
  };

  const handleReplyChange = (e, commentId) => {
    setReplyTexts((prev) => ({
      ...prev,
      [commentId]: e.target.value
    }));
  };

  const handleSendComment = async () => {
    if (mainComment.trim()) {
      const payload = {
        post_id: data?.id,
        comment: mainComment,
        tag: tagedUser?.map((prev) => prev.id)
      };

      try {
        await createComment(payload);
        setMainComment('');
        toast.success('Comment added successfully!');
        setUsersList([]);
        setTagedUser([]);
        onSuccess();
      } catch (error) {
        console.error('Error adding comment:', error);
        toast.error(error?.response?.data?.message || error?.message || 'Failed to add comment');
      }
    }
  };

  const handleSendReply = async (commentId) => {
    const replyText = replyTexts[commentId]?.trim();
    if (replyText) {
      const payload = {
        post_id: data?.id,
        reply: replyText,
        comment_id: commentId,
        tag: tagedUser?.map((prev) => prev.id)
      };

      try {
        await createReply(payload);
        setReplyTexts((prev) => ({ ...prev, [commentId]: '' }));
        setReplyingTo(null);
        toast.success('Reply added successfully!');
        setUsersList([]);
        setTagedUser([]);
        onSuccess();
      } catch (error) {
        console.error('Error adding reply:', error);
        toast.error(error?.response?.data?.message || error?.message || 'Failed to add reply');
      }
    }
  };

  const handleReply = (commentId) => {
    setReplyingTo(commentId);
  };

  const loadMoreComments = () => {
    setVisibleComments((prev) => prev + 2);
  };

  const toggleReplies = (commentId) => {
    setVisibleReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const handleToggle = () => {
    handlePublishPost();
  };

  const { mutate: publishPost, isLoading: isLoadingUserbyIdMutation } = useMutation({
    mutationFn: (post) => editPost(post),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: () => {
      setIsSubmitting(false);
      setIsPublic((prev) => !prev);
      toast.success(`Post visibility updated to ${!isPublic ? 'Public' : 'Private'}`);
      onSuccess();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error updating post visibility:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to update post visibility'
      );
    }
  });

  const handlePublishPost = () => {
    const updatedData = { ...data, is_public: !isPublic };
    publishPost(updatedData);
  };

  const toggleCommentInput = () => {
    setShowCommentInput((prev) => !prev);
  };

  const handleLike = async () => {
    const payload = { post_id: data?.id };

    try {
      if (isLiked) {
        await deleteLike(data?.id); // Call deleteLike if already liked
        setLikes((prev) => prev - 1); // Decrement likes
      } else {
        await createLike(payload); // Call createLike if not liked
        setLikes((prev) => prev + 1); // Increment likes
      }
      setIsLiked((prev) => !prev); // Toggle liked state
    } catch (error) {
      console.error('Error handling like:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to update like status'
      );
    }
  };

  const formatTimeDifference = (updatedAt) => {
    const currentTime = new Date();
    const updatedTime = new Date(updatedAt);
    const diffInMilliseconds = currentTime - updatedTime;
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));

    if (diffInHours >= 24) {
      const days = Math.floor(diffInHours / 24);
      const remainingHours = diffInHours % 24;
      return `${days}d${remainingHours > 0 ? ` ${remainingHours}h` : ''}`;
    } else if (diffInHours >= 1) {
      return `${diffInHours}h`;
    } else {
      return `${diffInMinutes}m`;
    }
  };

  const { mutate: handleFetchUsers } = useMutation({
    mutationFn: (id) => getUsersByChar(id),
    onSuccess: async (data) => {
      if (!data || data.length === 0) {
        // If response is empty, clear the list and hide the modal
        setUsersList([]);
        return;
      }

      // Extract id and name from the response
      const users = data.map((user) => ({
        id: user.id,
        name: user.name
      }));

      setUsersList((prev) => {
        const newList = [...prev, ...users];
        // Remove duplicates based on id
        return newList.filter(
          (user, index, self) => index === self.findIndex((u) => u.id === user.id)
        );
      });
    },
    onError: (err) => {
      console.error('Error fetching users', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error fetching users');
    }
  });

  const handleTagUser = async (data) => {
    if (!data) {
      setUsersList([]);
      return;
    }
    handleFetchUsers(data);
  };

  return (
    <div className="w-[500px] bg-white p-4 rounded-lg shadow-lg mx-auto">
      {/* Top Section */}
      <div className="topSection flex items-center gap-2">
        <Avatar name={data?.creater_name} size="40" round />
        <div className="info w-[100%]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">{data?.creater_name}</p>
              <p className="text-gray-400 text-xs">{data?.project_name}</p>
            </div>
            {tagInfo && (
              <div className="flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                <img
                  src={tagInfo.icon}
                  alt="tag"
                  className="w-3.5 h-3.5"
                  style={{ filter: 'brightness(0) saturate(100%)' }}
                />
                <span>{tagInfo.label}</span>
              </div>
            )}
          </div>
          <div className="flex justify-between w-[100%] mt-1">
            <p className="text-gray-400 text-xs">
              {new Intl.DateTimeFormat('en-GB', {
                day: '2-digit',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }).format(new Date(data?.updated_at))}
            </p>
          </div>
        </div>
        <div
          className="ml-auto flex items-end gap-2 flex-col"
          onClick={(e) => {
            handleToggle();
            e.stopPropagation();
          }}>
          <span
            className={`tag ${
              isPublic ? 'bg-blue-200 hover:bg-blue-300' : 'bg-red-200 hover:bg-red-300'
            } text-sm px-2 py-1 rounded cursor-pointer transition-all duration-200`}>
            {isPublic ? 'Public' : 'Private'}
          </span>
          <p className="text-gray-400 text-xs whitespace-nowrap">
            {formatTimeDifference(data?.updated_at) + ' ago '}
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="content flex flex-col gap-4 mt-4 relative">
        {/* Description */}
        <p className="desc text-sm">{data?.post}</p>

        {/* Image Slider or Single Image */}
        {imageAttachments && imageAttachments.length > 0 && (
          <div className="mt-4 relative">
            {imageAttachments.length === 1 ? (
              /* If there's only one image, just render it (no dots) */
              <img
                src={imageAttachments[0].url}
                alt={imageAttachments[0].name}
                className="w-full h-[368px] object-contain bg-black"
              />
            ) : (
              /* If more than one image, show Slider with dots */
              <Slider {...sliderSettings}>
                {imageAttachments.map((attach) => (
                  <div key={attach.id} className="flex justify-center items-center">
                    <img
                      src={attach.url}
                      alt={attach.name}
                      className="w-full h-auto object-contain bg-black"
                    />
                  </div>
                ))}
              </Slider>
            )}
          </div>
        )}

        {/* Files List Section */}
        {fileAttachments && fileAttachments.length > 0 && (
          <div className="mt-4">
            <p className="font-medium text-sm mb-2">Files</p>
            {fileAttachments.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between bg-gray-50 p-2 rounded mb-2"
                onClick={(e) => e.stopPropagation()}>
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline text-sm">
                  {file.name}
                </a>
                <a href={file.url} download={file.name} className="text-white rounded text-xs">
                  <img src={DownloadIcon} alt="" />
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Separator Line */}
        <div className="border-t border-gray-300 " />

        {/* Like and Comment Buttons */}
        <div className="flex justify-center">
          <button
            onClick={(e) => {
              handleLike();
              e.stopPropagation();
            }}
            className="flex items-center text-black mr-20">
            <img src={isLiked ? FilledLikeIcon : LikeIcon} alt="Like" className="h-5 w-5 mr-1" />
            Like {likes > 0 && `(${likes})`}
          </button>
          <button
            onClick={(e) => {
              toggleCommentInput();
              e.stopPropagation();
            }}
            className="flex items-center text-black">
            <img src={CommentIcon} alt="Comment" className="h-5 w-5 mr-1" />
            Comment {comments.length > 0 && `(${comments.length})`}
          </button>
        </div>

        {/* Comment Input */}
        {showCommentInput && (
          <div
            className="mt-4 flex items-center border rounded-lg px-4 py-2 relative"
            onClick={(e) => e.stopPropagation()}>
            {userList.length > 0 && mainComment !== '' && (
              <div
                className="absolute flex flex-col justify-start items-start  rounded-[10px] shadow-lg bg-white p-2 top-10 max-h-[300px] overflow-y-auto z-10 min-w-[220px] w-full"
                onClick={(e) => e.stopPropagation()}>
                {userList.map((u) => (
                  <div key={u.id} className="w-full">
                    <div
                      className="w-full flex items-center gap-3 p-2 hover:bg-gray-100 cursor-pointer rounded"
                      onClick={() => {
                        if (mainComment) {
                          setTagedUser((prev) =>
                            prev.some((tagged) => tagged.id === u.id) ? prev : [...prev, u]
                          );
                          if (!mainComment.includes(`@${u.name.replace(/\s+/g, '_')}`)) {
                            const newValue = mainComment.replace(
                              /@\w*$/,
                              `@${u.name.replace(/\s+/g, '_')} `
                            );
                            setMainComment(newValue);
                            setUsersList([]);
                          }
                        }
                      }}>
                      <Avatar name={u.name} size="24" round />
                      <span className="text-sm text-gray-800">{u.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <TextareaAutosize
              value={mainComment}
              onChange={(e) => {
                const text = e.target.value;
                setMainComment(text);

                // Remove users if their @name is not present in the comment
                setTagedUser((prev) =>
                  prev.filter((user) => {
                    // Use regex to check if @name is present (handle spaces and special characters)
                    const namePattern = new RegExp(`@${user.name.replace(/\s+/g, '_')}(\\s|$)`);
                    return namePattern.test(text);
                  })
                );

                // Match the last @mention pattern for suggestions
                const matches = text.match(/@(\w*)$/);
                if (matches) {
                  const latestMention = matches[1];
                  handleTagUser(latestMention);
                } else {
                  setUsersList([]);
                }
              }}
              className="flex-1 text-sm border-none outline-none resize-none overflow-hidden"
              placeholder="Add a comment..."
              minRows={1}
            />

            <button
              onClick={(e) => {
                handleSendComment();
                e.stopPropagation();
              }}
              className="ml-2">
              <img src={MsgSend} alt="Send" className="h-[20px]" />
            </button>
          </div>
        )}
      </div>

      {/* Comment Section */}
      <div className="comments mt-4">
        {comments.slice(0, visibleComments).map((comment) => (
          <div key={comment.id} className="comment mb-4">
            <div className="flex items-start gap-2">
              <Avatar name={comment?.creater_name} size="32" round />
              <div className="flex-1 relative">
                <p className="text-sm font-medium">{comment?.creater_name}</p>
                <p className="text-xs text-gray-400">{data?.project_name}</p>
                {comment?.comment.split(/(@\w+)/g).map((part, index) =>
                  part.startsWith('@') ? (
                    <span key={index} className="text-sm mt-1 text-blue-500">
                      {part}
                    </span>
                  ) : (
                    <span key={index} className="text-sm mt-1">
                      {part}
                    </span>
                  )
                )}

                <div className="flex gap-1">
                  <p className="text-xs mt-1"> {formatTimeDifference(comment?.updated_at) + ' '}</p>
                  <button
                    onClick={(e) => {
                      handleReply(comment?.id);
                      e.stopPropagation();
                    }}
                    className="text-blue-500 text-xs mt-1">
                    Reply {comment?.replies?.length > 0 && `(${comment.replies.length})`}
                  </button>
                </div>

                {/* Replies Section */}
                {Array.isArray(comment?.replies) && comment?.replies?.length > 0 && (
                  <div className="replies mt-2 border-l-2 border-gray-200 pl-4">
                    {comment?.replies?.length > 2 && !visibleReplies[comment.id] ? (
                      <button
                        onClick={() => toggleReplies(comment.id)}
                        className="text-blue-500 text-xs mt-1">
                        See previous replies
                      </button>
                    ) : (
                      comment?.replies?.map((reply) => (
                        <div key={reply.id} className="reply mb-2">
                          <div className="flex items-start gap-2">
                            <Avatar name={reply.creater_name} size="28" round />
                            <div>
                              <p className="text-sm font-medium">{reply.creater_name}</p>
                              <p className="text-xs text-gray-400">{data?.project_name}</p>
                              {reply?.reply.split(/(@\w+)/g).map((part, index) =>
                                part.startsWith('@') ? (
                                  <span key={index} className="text-sm mt-1 text-blue-400">
                                    {part}
                                  </span>
                                ) : (
                                  <span key={index} className="text-sm mt-1">
                                    {part}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Reply Input */}
                {replyingTo === comment.id && (
                  <>
                    {userList.length > 0 && replyTexts[comment.id] !== '' && (
                      <div
                        className="absolute flex flex-col justify-start items-start  rounded-[10px] shadow-lg bg-white p-2 top-[8rem] max-h-[300px] overflow-y-auto z-50 min-w-[220px] w-full"
                        onClick={(e) => e.stopPropagation()}>
                        {userList.map((u) => (
                          <div key={u.id} className="w-full">
                            <div
                              className="w-full flex items-center gap-3 p-2 hover:bg-gray-100 cursor-pointer rounded"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (replyTexts[comment.id]) {
                                  setTagedUser((prev) =>
                                    prev.some((tagged) => tagged.id === u.id) ? prev : [...prev, u]
                                  );
                                  if (
                                    !replyTexts[comment.id].includes(
                                      `@${u.name.replace(/\s+/g, '_')}`
                                    )
                                  ) {
                                    const newValue = replyTexts[comment.id].replace(
                                      /@\w*$/,
                                      `@${u.name.replace(/\s+/g, '_')} `
                                    );
                                    setReplyTexts((prev) => ({
                                      ...prev,
                                      [comment.id]: newValue
                                    }));
                                    setUsersList([]);
                                  }
                                }
                              }}>
                              <Avatar name={u.name} size="22" round />
                              <span className="text-sm text-gray-800">{u.name}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div
                      className="mt-2 flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}>
                      <TextareaAutosize
                        value={replyTexts[comment.id] || ''}
                        onChange={(e) => {
                          const text = e.target.value;
                          setReplyTexts((prev) => ({
                            ...prev,
                            [comment.id]: text
                          }));

                          // Remove users if their @name is not present in the comment
                          setTagedUser((prev) =>
                            prev.filter((user) => {
                              // Use regex to check if @name is present (handle spaces and special characters)
                              const namePattern = new RegExp(
                                `@${user.name.replace(/\s+/g, '_')}(\\s|$)`
                              );
                              return namePattern.test(text);
                            })
                          );

                          // Match the last @mention pattern for suggestions
                          const matches = text.match(/@(\w*)$/);
                          if (matches) {
                            const latestMention = matches[1];
                            handleTagUser(latestMention);
                          } else {
                            setUsersList([]);
                          }
                        }}
                        className="flex-1 text-sm border rounded-lg px-2 py-1 resize-none"
                        placeholder="Write a reply..."
                        minRows={1}
                      />

                      <button onClick={() => handleSendReply(comment.id)}>
                        <img src={MsgSend} alt="Send" className="h-[20px]" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        {visibleComments < comments?.length && (
          <button
            onClick={(e) => {
              loadMoreComments();
              e.stopPropagation();
            }}
            className="text-blue-500 text-sm mt-4">
            Load more comments
          </button>
        )}
      </div>
    </div>
  );
}

export default LiveFeedCard;
